import axios from 'axios';
import { createContext, Dispatch, SetStateAction, useEffect, useState } from 'react';
import COLORS from '../constants/colors.json';

export const page1Template = [
  {
    id: 0,
    label: 'Is the tumor volume accurately segmented?',
    type: 'tree',
    condition: null,
    options: [
      { id: 'Yes', label: 'Yes', terminal: false },
      { id: 'No', label: 'No', terminal: false },
    ],
    selected: null,
    completed: false,
    display_info: false,
  },
  {
    id: 1,
    label: 'How would you rate the size of the tumor? Small, medium, large?',
    type: 'radio',
    condition: { level_id: 0 },
    options: [
      { id: 0, label: 'Small' },
      { id: 1, label: 'Medium' },
      { id: 2, label: 'Large' },
    ],
    selected: null,
    completed: false,
    display_info: false,
  },
  {
    id: 2,
    label: 'Which lobe of the brain is the tumor located in? Frontal, parietal, occipital, temporal?',
    type: 'radio',
    condition: { level_id: 1 },
    options: [
      { id: 0, label: 'Frontal' },
      { id: 1, label: 'Parietal' },
      { id: 2, label: 'Occipital' },
      { id: 3, label: 'Temporal' },
    ],
    selected: null,
    completed: false,
    display_info: false,
  },
  {
    id: 3,
    label: 'What number of lesions can you identify from the MRI?',
    type: 'radio',
    condition: { level_id: 2 },
    options: [
      { id: 0, label: '0' },
      { id: 1, label: '1' },
      { id: 2, label: '2' },
      { id: 3, label: '3' },
      { id: 4, label: '4' },
      { id: 5, label: '5' },
    ],
    selected: null,
    completed: false,
    display_info: false,
  },
  {
    id: 4,
    label: 'Are there any artifacts on the MRI image? Please rate the image quality as follows:',
    type: 'radio-grid',
    condition: { level_id: 3 },
    rows: [
      { id: 'motion_blur', label: 'Motion Blur', selected: null, color: COLORS[0] },
      { id: 'susceptibility_artifacts', label: 'Susceptibility Artifacts', selected: null, color: COLORS[1] },
      { id: 'ghosting', label: 'Ghosting', selected: null, color: COLORS[4] },
    ],
    columns: [
      { value: 0, label: '0: None' },
      { value: 1, label: '1: Mild' },
      { value: 2, label: '2: Moderate' },
      { value: 3, label: '3: Severe' },
    ],
    completed: false,
    display_info: false,
  },
  {
    id: 5,
    label:
      'On a scale of 1 to 5, with 5 being the highest, how likely is the segmented tumor in this image to cause postoperative paresis in the patient?',
    type: 'radio',
    condition: { level_id: 4 },
    options: [
      { id: 0, label: '1' },
      { id: 1, label: '2' },
      { id: 2, label: '3' },
      { id: 3, label: '4' },
      { id: 4, label: '5' },
    ],
    selected: null,
    completed: false,
    display_info: false,
  },
  {
    id: 6,
    label:
      'To what extent does the tumor\'s size, as depicted in this image, contribute to your evaluation of postoperative paresis risk? (1 = Minimal contribution, 5 = Substantial contribution)',
    type: 'radio',
    condition: { level_id: 5 },
    options: [
      { id: 0, label: '1' },
      { id: 1, label: '2' },
      { id: 2, label: '3' },
      { id: 3, label: '4' },
      { id: 4, label: '5' },
    ],
    selected: null,
    completed: false,
    display_info: false,
  },
  {
    id: 7,
    label:
      'How strongly does the tumor\'s apparent infiltration of surrounding tissue, as seen in this image, factor into your assessment of postoperative paresis risk? (1 = Not at all, 5 = Very strongly)',
    type: 'radio',
    condition: { level_id: 6 },
    options: [
      { id: 0, label: '1' },
      { id: 1, label: '2' },
      { id: 2, label: '3' },
      { id: 3, label: '4' },
      { id: 4, label: '5' },
    ],
    selected: null,
    completed: false,
    display_info: false,
  },
  {
    id: 8,
    label:
      'To what extent does the tumor\'s location, as segmented in this image, correlate with an increased risk of postoperative motor deficits? (1 = No correlation, 5 = Strong correlation)',
    type: 'radio',
    condition: { level_id: 7 },
    options: [
      { id: 0, label: '1' },
      { id: 1, label: '2' },
      { id: 2, label: '3' },
      { id: 3, label: '4' },
      { id: 4, label: '5' },
    ],
    selected: null,
    completed: false,
    display_info: false,
  },
  {
    id: 9,
    label:
      'Based on the imaging characteristics of this tumor, how challenging do you anticipate the surgical resection to be in terms of preserving motor function? (1 = Not challenging, 5 = Extremely challenging)',
    type: 'radio',
    condition: { level_id: 8 },
    options: [
      { id: 0, label: '1' },
      { id: 1, label: '2' },
      { id: 2, label: '3' },
      { id: 3, label: '4' },
      { id: 4, label: '5' },
    ],
    selected: null,
    completed: false,
    display_info: false,
  },
  {
    id: 10,
    label:
      'To what degree does the tumor\'s proximity to eloquent motor areas, as visualized in this image, influence your surgical approach? (1 = No influence, 5 = Significant influence)',
    type: 'radio',
    condition: { level_id: 9 },
    options: [
      { id: 0, label: '1' },
      { id: 1, label: '2' },
      { id: 2, label: '3' },
      { id: 3, label: '4' },
      { id: 4, label: '5' },
    ],
    selected: null,
    completed: false,
    display_info: false,
  },
  {
    id: 11,
    label:
      'How important would you consider this imaging data in your overall clinical decision-making process regarding the management of potential postoperative paresis? (1 = Not important, 5 = Critically important)',
    type: 'radio',
    condition: { level_id: 10 },
    options: [
      { id: 0, label: '1' },
      { id: 1, label: '2' },
      { id: 2, label: '3' },
      { id: 3, label: '4' },
      { id: 4, label: '5' },
    ],
    selected: null,
    completed: false,
    display_info: false,
  },
  {
    id: 12,
    label:
      'How confident are you in predicting the risk of postoperative paresis based solely on this imaging data? (1 = Not confident at all, 5 = Very confident)',
    type: 'radio',
    condition: { level_id: 11 },
    options: [
      { id: 0, label: '1' },
      { id: 1, label: '2' },
      { id: 2, label: '3' },
      { id: 3, label: '4' },
      { id: 4, label: '5' },
    ],
    selected: null,
    completed: false,
    display_info: false,
  },
  {
    id: 13,
    label:
      'How valuable would additional imaging modalities (e.g., DTI, fMRI) be in assessing the risk of postoperative paresis for this case? (1 = Not valuable, 5 = Extremely valuable)',
    type: 'radio',
    condition: { level_id: 12 },
    options: [
      { id: 0, label: '1' },
      { id: 1, label: '2' },
      { id: 2, label: '3' },
      { id: 3, label: '4' },
      { id: 4, label: '5' },
    ],
    selected: null,
    completed: false,
    display_info: false,
  },
];


export const parentTemplate = {
  lockOnCompletion: true,
  completed: false,
  steps: [
    {
      id: 1,
      type: 'questionnaire',
      completed: false,
      template: page1Template,
      description: 'Please respond the following questions based on the displayed MRT',
    },
    {
      id: 2,
      type: 'submit',
      completed: false,
      template: [],
      description: 'You have completed the annotations for this case.',
    },
  ],
};

export const generatePointAnnotationTemplate = (annotationTree: any) => {
  const questionnaireStep = annotationTree.find((step: any) => step.id === 1);
  const annotationStep = annotationTree.find((step: any) => step.id === 2);
  annotationStep.template = [];

  const malignancyCriteriaLevels = questionnaireStep.template.filter((level: any) => level.id === 1 || level.id === 2);

  let lastRowId = null;
  for (const malignancyCriteriaLevel of malignancyCriteriaLevels) {
    if (malignancyCriteriaLevel.type === 'radio-grid') {
      for (const row of malignancyCriteriaLevel.rows) {
        if (row.selected && row.selected !== 0) {
          let condition = null;
          if (lastRowId) {
            condition = {
              level_id: lastRowId,
            };
          }
          annotationStep.template.push({
            type: 'tree',
            id: row.id,
            label: row.label,
            color: row.color,
            options: [
              {
                id: 0,
                value: 0,
                label: 'Not visibile',
              },
              {
                id: 1,
                value: 1,
                label: 'Visible',
                action: {
                  description: 'Please mark the structure on one representative area in the image',
                  type: 'annotation',
                  tool: 'point',
                },
              },
            ],
            selected: null,
            coordinates: [],
            annotoriousFormat: null,
            condition,
            completed: false,
          });
          lastRowId = row.id;
        }
      }
    }
  }
  if (annotationStep.template.length === 0) {
    annotationStep.completed = true;
  } else {
    annotationStep.completed = false;
  }
  return annotationStep;
};

interface AnnotationContextInterface {
  annotation: any;
  getAnnotation: (stageId: number, sequenceId: number, alertCallback: (message: string) => void) => void;
  updateAnnotation: (alertCallback: (message: string) => void) => void;
  currentAnnotationStep: any;
  currentAnnotationStepIndex: number;
  annotationLength: number;
  tempUpdateAnnotation: (updatedAnnotationStep: any) => void;
  getPreviousAnnotationStep: () => void;
  getNextAnnotationStep: () => void;
  currentAction: any;
  setCurrentAction: Dispatch<SetStateAction<any>>;
  saveCoordinates: (coordinates: any, annotoriousFormat: any) => void;
  updateCoordinates: (coordinates: any, annotoriousFormat: any) => void;
  currentAnnotation: any;
  annotationExists: boolean;
  currentTime: number;
  setCurrentTime: Dispatch<SetStateAction<number>>;
  originalAnnotation: any;
}

export const AnnotationContext = createContext<AnnotationContextInterface>({
  annotation: null,
  getAnnotation: () => undefined,
  updateAnnotation: () => undefined,
  currentAnnotationStep: null,
  currentAnnotationStepIndex: -1,
  annotationLength: 0,
  tempUpdateAnnotation: () => undefined,
  getPreviousAnnotationStep: () => undefined,
  getNextAnnotationStep: () => undefined,
  currentAction: null,
  setCurrentAction: () => undefined,
  saveCoordinates: () => undefined,
  updateCoordinates: () => undefined,
  currentAnnotation: null,
  annotationExists: false,
  currentTime: 0,
  setCurrentTime: () => undefined,
  originalAnnotation: null,
});

export const AnnotationProvider = ({ children }: any) => {
  const [annotation, setAnnotation] = useState<any>();
  const [annotationExists, setAnnotationExists] = useState(false);
  const [annotationLength, setAnnotationLength] = useState(0);
  const [currentAnnotationStep, setCurrentAnnotationStep] = useState<any>();
  const [currentAnnotationStepIndex, setCurrentAnnotationStepIndex] = useState(0);
  const [currentAction, setCurrentAction] = useState(null);
  const [currentAnnotation, setCurrentAnnotation] = useState<any>(null);
  const [currentTime, setCurrentTime] = useState(0);
  const [originalAnnotation, setOriginalAnnotation] = useState<any>(null);

  useEffect(() => {
    if (!annotation) return;
    const newAnnotationStep = { ...annotation.annotation_tree.steps[currentAnnotationStepIndex] };
    if (newAnnotationStep.template.length === 0) {
      newAnnotationStep.completed = true;
    }
    setCurrentAnnotationStep(newAnnotationStep);
    setAnnotationLength(annotation.annotation_tree.steps.length);
  }, [annotation]);

  useEffect(() => {
    if (!annotation) return;
    console.log('currentAnnotationStepIndex changed: ' + currentAnnotationStepIndex);
    console.log('annotation: ' + annotation);
    const newAnnotationStep = { ...annotation.annotation_tree.steps[currentAnnotationStepIndex] };
    if (newAnnotationStep.template.length === 0) {
      newAnnotationStep.completed = true;
    }
    setCurrentAnnotationStep(newAnnotationStep);
  }, [currentAnnotationStepIndex]);

  const saveCoordinates = (coordinates: any, annotoriousFormat: any) => {
    setCurrentAnnotation({ coordinates, annotoriousFormat });
    setCurrentAction(null);
  };

  const updateCoordinates = (coordinates: any, annotoriousFormat: any) => {
    setCurrentAnnotationStep((currentAnnotationStep: any) => {
      if (currentAnnotationStep.type !== 'annotation') return currentAnnotationStep;
      const templateIndex = currentAnnotationStep.template.findIndex((temp: any) => {
        return temp.annotoriousFormat?.id === annotoriousFormat.id;
      });
      if (templateIndex === -1) return currentAnnotationStep;
      const updatedAnnotationStep = { ...currentAnnotationStep };
      updatedAnnotationStep.template[templateIndex].coordinates = coordinates;
      updatedAnnotationStep.template[templateIndex].annotoriousFormat = annotoriousFormat;
      return updatedAnnotationStep;
    });
  };

  const getAnnotation = async (stageId: number, sequenceId: number, alertCallback: (message: string) => void) => {
    try {
      const res = await axios.get(`/stage/${stageId}/sequence/${sequenceId}/annotation`);
      setAnnotation(res.data);
      setAnnotationExists(true);
      setOriginalAnnotation(res.data);
      setAnnotationLength(res.data.annotation_tree.steps.length);
      setCurrentAnnotationStepIndex(1);
    } catch (err) {
      if (axios.isAxiosError(err)) {
        if (err.response?.status === 404) {
          const annotationTemplate = {
            annotation_tree: JSON.parse(JSON.stringify(parentTemplate)),
            time: 0,
            sequence_id: sequenceId,
            stage_id: stageId,
          };
          setAnnotation(annotationTemplate);
          setAnnotationExists(false);
          setOriginalAnnotation(annotationTemplate);
          setAnnotationLength(annotationTemplate.annotation_tree.steps.length);
          setCurrentAnnotationStepIndex(0);
          return;
        }
      }
      alertCallback('An error occured while fetching annotation');
      console.error(err);
    }
  };

  const tempUpdateAnnotation = (updatedAnnotationStep: any) => {
    const tempAnnotation = { ...annotation };
    tempAnnotation.annotation_tree.steps[currentAnnotationStepIndex] = updatedAnnotationStep;
    if (updatedAnnotationStep.type === 'questionnaire') {
      tempAnnotation.annotation_tree.steps[currentAnnotationStepIndex + 1] = generatePointAnnotationTemplate(
        tempAnnotation.annotation_tree.steps
      );
    }

    setAnnotation(tempAnnotation);
  };

  const updateAnnotation = async (alertCallback: (message: string) => void) => {
    try {
      if (annotationExists) {
        await axios.put(
          `/annotation/${annotation.id}`,
          JSON.stringify({
            stage_id: annotation.stage_id,
            sequence_id: annotation.sequence_id,
            time: currentTime,
            annotation_tree: annotation.annotation_tree,
          })
        );
      } else {
        await axios.post('/annotation', JSON.stringify({ ...annotation, time: currentTime }));
        //setAnnotationExists(true);
      }
    } catch (err) {
      console.error(err);
      alertCallback('An error occured while saving annotation');
    }
  };

  const getNextAnnotationStep = () => {
    setCurrentAnnotationStepIndex(currentAnnotationStepIndex + 1);
  };

  const getPreviousAnnotationStep = () => {
    // reset current annotation step
    const newAnnotationStep = { ...currentAnnotationStep };
    newAnnotationStep.completed = false;
    for (const category of newAnnotationStep.template) {
      category.coordinates = [];
      category.annotoriousFormat = null;
      category.selected = null;
      category.completed = false;
    }
    tempUpdateAnnotation(newAnnotationStep);
    setCurrentAnnotationStepIndex(currentAnnotationStepIndex - 1);
  };

  const value = {
    annotation,
    getAnnotation,
    updateAnnotation,
    annotationLength,
    currentAnnotationStep,
    currentAnnotationStepIndex,
    tempUpdateAnnotation,
    getPreviousAnnotationStep,
    getNextAnnotationStep,
    currentAction,
    setCurrentAction,
    saveCoordinates,
    currentAnnotation,
    annotationExists,
    currentTime,
    setCurrentTime,
    originalAnnotation,
    updateCoordinates,
  };

  return <AnnotationContext.Provider value={value}>{children}</AnnotationContext.Provider>;
};
